/**
 * Tenant Switcher
 *
 * Lets a login that belongs to several workspaces choose which one it is acting in — the
 * consultant case that multi-tenancy exists for.
 *
 * Renders nothing when the user belongs to one workspace, which is the common case: a control
 * offering a single option is noise, and it would appear for every client user.
 *
 * Switching replaces the session token (the server issues one scoped to the target tenant),
 * so the whole app must re-read its data afterwards. Rather than trying to invalidate every
 * cache and in-flight request, this reloads the page — the honest and reliable option, and
 * rare enough that the cost does not matter.
 *
 * Laid out for its one mount point: the Sidebar footer. Full width of its container, and the
 * list opens upward, because at the bottom of the viewport a downward menu is off-screen.
 */

import React, { useState } from 'react';
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const TenantSwitcher: React.FC = () => {
  const { memberships, activeTenantId, hasMultipleTenants, switchTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // One workspace (or none) needs no switcher.
  if (!hasMultipleTenants) return null;

  // Only active memberships are switchable, and `hasMultipleTenants` counts the same set —
  // list anything wider and the control offers a workspace the server will refuse.
  const switchable = memberships.filter((m) => m.status === 'active');

  // Resolved against the unfiltered list on purpose: if the active membership has just been
  // deactivated the label should still name where the session currently is.
  const active = memberships.find((m) => m.tenant_id === activeTenantId);

  const handleSwitch = async (tenantId: string) => {
    if (tenantId === activeTenantId) {
      setOpen(false);
      return;
    }
    setSwitching(tenantId);
    setError(null);
    try {
      await switchTenant(tenantId);
      // Full reload: the new token changes what every endpoint returns, so anything already
      // fetched under the previous tenant is stale by definition.
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not switch workspace');
      setSwitching(null);
    }
  };

  return (
    <div className="relative w-full">
      {/* The closed control shows only an icon and a workspace name, which reads as a label
          rather than something to click — and it first appears the moment a second workspace
          is granted, exactly when the user is looking for a way in. The caption names it.
          aria-hidden because the button already carries an aria-label. */}
      <span
        className="block px-1 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
        aria-hidden="true"
      >
        Workspace
      </span>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Switch workspace"
        className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
      >
        <Building2 className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 min-w-0 truncate text-left">{active?.tenant_name ?? 'Select workspace'}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <>
          {/* Click-away layer */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <ul
            role="listbox"
            className="absolute left-0 right-0 bottom-full z-20 mb-2 max-h-[60vh] overflow-y-auto py-1 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          >
            <li className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Workspaces
            </li>
            {switchable.map((m) => {
              const isActive = m.tenant_id === activeTenantId;
              const isBusy = switching === m.tenant_id;
              return (
                <li key={m.tenant_id} role="option" aria-selected={isActive}>
                  <button
                    type="button"
                    disabled={!!switching}
                    onClick={() => handleSwitch(m.tenant_id)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 ${
                      isActive ? 'font-semibold text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{m.tenant_name}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {m.tenant_role}
                      </span>
                    </span>
                    {isBusy ? (
                      <Loader2 className="w-4 h-4 shrink-0 animate-spin" aria-hidden="true" />
                    ) : isActive ? (
                      <Check className="w-4 h-4 shrink-0" aria-hidden="true" />
                    ) : null}
                  </button>
                </li>
              );
            })}
            {error && (
              <li className="px-3 py-2 text-xs text-red-600 dark:text-red-400" role="alert">
                {error}
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
};

export default TenantSwitcher;
